import type { AppMode } from '@iheartjane/types';
import type {
  DefinedUseQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type {
  GetTargetedBrandSpecialsReturnType,
  QueryBody,
} from '@jane/shared/data-access';
import {
  getTargetedBrandSpecials,
  targetedBrandSpecialsKeys,
} from '@jane/shared/data-access';

interface TargetedBrandSpecialsQueryProps {
  appMode: AppMode;
  page?: number;
  perPage?: number;
  storeIds: (string | number)[];
}

type GetTargetedBrandSpecialsQueryBody = QueryBody<
  GetTargetedBrandSpecialsReturnType | null,
  unknown,
  GetTargetedBrandSpecialsReturnType,
  (string | number | undefined)[]
>;

type TargetedBrandSpecialsQueryOptions = Pick<
  GetTargetedBrandSpecialsQueryBody,
  'initialData'
>;

/* Query Body Overloads */
function getTargetedBrandSpecialsQueryBody(
  params: TargetedBrandSpecialsQueryProps | undefined,
  queryOptions?: Omit<TargetedBrandSpecialsQueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): GetTargetedBrandSpecialsQueryBody;

function getTargetedBrandSpecialsQueryBody(
  params: TargetedBrandSpecialsQueryProps | undefined,
  queryOptions?: Omit<TargetedBrandSpecialsQueryOptions, 'initialData'> & {
    initialData?:
      | GetTargetedBrandSpecialsReturnType
      | (() => GetTargetedBrandSpecialsReturnType);
  }
): GetTargetedBrandSpecialsQueryBody;

function getTargetedBrandSpecialsQueryBody(
  params: TargetedBrandSpecialsQueryProps | undefined,
  queryOptions: any = {}
) {
  const { appMode, storeIds, page, perPage } = params ?? {};

  return {
    enabled: !!storeIds?.length && !!appMode,
    queryFn: () =>
      getTargetedBrandSpecials({ appMode, storeIds, page, perPage }),
    queryKey: targetedBrandSpecialsKeys.brandSpecials({
      appMode,
      storeIds,
      page,
      perPage,
    }),
    ...queryOptions,
  };
}

/* Hook Overloads */
function useTargetedBrandSpecials(
  params: TargetedBrandSpecialsQueryProps,
  queryOptions?: Omit<TargetedBrandSpecialsQueryOptions, 'initialData'> & {
    initialData?: () => undefined;
  }
): UseQueryResult<GetTargetedBrandSpecialsReturnType | null, unknown>;

function useTargetedBrandSpecials(
  params: TargetedBrandSpecialsQueryProps,
  queryOptions?: Omit<TargetedBrandSpecialsQueryOptions, 'initialData'> & {
    initialData:
      | GetTargetedBrandSpecialsReturnType
      | (() => GetTargetedBrandSpecialsReturnType);
  }
): DefinedUseQueryResult<GetTargetedBrandSpecialsReturnType, unknown>;

function useTargetedBrandSpecials(
  params: TargetedBrandSpecialsQueryProps,
  queryOptions?: object
) {
  return useQuery(getTargetedBrandSpecialsQueryBody(params, queryOptions));
}

export function useTargetedBrandSpecialsForStore(
  storeId: string | number,
  queryOptions?: object
) {
  return useTargetedBrandSpecials(
    { storeIds: [storeId], appMode: 'kiosk' },
    queryOptions
  );
}
